/**
 * Presentation helpers for Call Details → Application Status → candidate test rows
 * (`GET call/details/:candidateId/candidate-tests`).
 */

const normalize = value =>
    String(value ?? "").trim().toLowerCase();

const normalizeKey = value =>
    normalize(value).replace(/[\s_-]+/g, "");

const includesAny = (value, matches) =>
    matches.some(match => value.includes(match));

const pickFirstNonEmpty = (...values) => {
    for (const value of values) {
        const normalized = String(value ?? "").trim();
        if (normalized && normalized.toLowerCase() !== "null") {
            return normalized;
        }
    }
    return "";
};

const STATUS_COLOR_MAP = {
    success: ["yes", "success", "pass", "complete", "completed", "positive_ok"],
    danger: ["no", "fail", "failed", "danger", "rejected"],
    warning: ["pending", "warning", "in_progress", "inprogress", "paperwork_sent"],
    info: ["info", "scheduled", "neutral"],
    secondary: ["secondary", "inactive", "unknown"],
    primary: ["primary"],
};

const STATUS_ROW_BACKGROUND_MAP = {
    drugtest: "status-item bg-success bg-opacity-10 status-item-hght",
    videointerview: "status-item bg-warning bg-opacity-10 status-item-hght",
    dotmedical: "status-item bg-warning bg-opacity-10 status-item-hght",
    dotmedicalcard: "status-item bg-warning bg-opacity-10 status-item-hght",
    medicalcard: "status-item bg-warning bg-opacity-10 status-item-hght",
    roadtest: "status-item bg-light status-item-hght",
    qualscert: "status-item bg-light status-item-hght",
    quarlscert: "status-item bg-light status-item-hght",
    quarlscertificate: "status-item bg-light status-item-hght",
    qualificationcertificate: "status-item bg-light status-item-hght",
};

export const parseCandidateTestsApiResponse = candidateTestsApiResponse => {
    const payload = candidateTestsApiResponse?.data ?? candidateTestsApiResponse;

    if (!payload || typeof payload !== "object") {
        return {
            rows: [],
            candidateClientId: "",
            candidateId: "",
        };
    }

    return {
        rows: Array.isArray(payload.rows)
            ? payload.rows
            : Array.isArray(payload)
              ? payload
              : [],
        candidateClientId: String(payload.candidateClientId ?? "").trim(),
        candidateId: String(payload.candidateId ?? "").trim(),
    };
};

export const extractCandidateApplicationStatusTestRowsFromApiResponse =
    candidateTestsApiResponse =>
        parseCandidateTestsApiResponse(candidateTestsApiResponse).rows;

export const sortCandidateApplicationStatusTestsByServerSortOrder =
    applicationStatusTestRows =>
        [...(Array.isArray(applicationStatusTestRows)
            ? applicationStatusTestRows
            : [])].sort(
            (left, right) =>
                (Number(left?.sort ?? 0) || 0) -
                (Number(right?.sort ?? 0) || 0)
        );

export const mapStatusColorKeyToReactstrapBadgeColor = statusColorKey => {
    const normalized = normalize(statusColorKey);

    return (
        Object.entries(STATUS_COLOR_MAP).find(([, values]) =>
            values.includes(normalized)
        )?.[0] ?? "secondary"
    );
};

export const mapWellKnownCategoryToStatusRowBackgroundClassNames =
    wellKnownCategory =>
        STATUS_ROW_BACKGROUND_MAP[normalizeKey(wellKnownCategory)] ??
        "status-item bg-light status-item-hght";

const ROW_HIGHLIGHT_BACKGROUND_CLASS_MAP = {
    warning: "status-item status-item-hght cd-test-row-highlight--warning",
    success: "status-item status-item-hght cd-test-row-highlight--success",
    neutral: "status-item status-item-hght cd-test-row-highlight--neutral",
    danger: "status-item status-item-hght cd-test-row-highlight--danger",
    progress: "status-item status-item-hght cd-test-row-highlight--progress",
};

const ROW_HIGHLIGHT_BADGE_CLASS_MAP = {
    warning: "badge-padding cd-test-badge--warning",
    success: "badge-padding cd-test-badge--success",
    neutral: "badge-padding cd-test-badge--neutral",
    danger: "badge-padding cd-test-badge--danger",
    progress: "badge-padding cd-test-badge--progress",
};

const BADGE_COLOR_TO_ROW_HIGHLIGHT_KEY = {
    success: "success",
    danger: "danger",
    warning: "warning",
    info: "neutral",
    secondary: "neutral",
    primary: "progress",
};

/** Light row background from API `rowHighlightKey` (warning | success | neutral | danger). */
export const mapRowHighlightKeyToStatusRowBackgroundClassNames = rowHighlightKey => {
    const normalized = normalize(rowHighlightKey);
    return ROW_HIGHLIGHT_BACKGROUND_CLASS_MAP[normalized] ?? null;
};

/** Prefer `rowHighlightKey` background; fall back to legacy `wellKnownCategory` mapping. */
export const resolveCandidateTestRowBackgroundClassNames = (
    applicationStatusTestRow = {}
) =>
    mapRowHighlightKeyToStatusRowBackgroundClassNames(
        applicationStatusTestRow?.rowHighlightKey
    ) ??
    mapWellKnownCategoryToStatusRowBackgroundClassNames(
        applicationStatusTestRow?.wellKnownCategory
    );

/** Badge pill class aligned with the row's `rowHighlightKey` (or derived status color). */
export const resolveCandidateApplicationStatusTestBadgeClassNames = (
    applicationStatusTestRow = {}
) => {
    const fromHighlight = normalize(
        applicationStatusTestRow?.rowHighlightKey
    );

    if (ROW_HIGHLIGHT_BADGE_CLASS_MAP[fromHighlight]) {
        return ROW_HIGHLIGHT_BADGE_CLASS_MAP[fromHighlight];
    }

    const bootstrapColor =
        resolveCandidateApplicationStatusTestBadgeColor(
            applicationStatusTestRow
        );

    const highlightKey =
        BADGE_COLOR_TO_ROW_HIGHLIGHT_KEY[bootstrapColor] ?? "neutral";

    return (
        ROW_HIGHLIGHT_BADGE_CLASS_MAP[highlightKey] ??
        ROW_HIGHLIGHT_BADGE_CLASS_MAP.neutral
    );
};

export const resolveCandidateApplicationStatusTestBadgeColor =
    applicationStatusTestRow => {
        const fromKey = mapStatusColorKeyToReactstrapBadgeColor(
            applicationStatusTestRow?.statusColorKey
        );

        if (fromKey && fromKey !== "secondary") {
            return fromKey;
        }

        const combinedRaw = normalize(`
            ${applicationStatusTestRow?.statusLabel ?? ""}
            ${applicationStatusTestRow?.rawStatus ?? ""}
            ${applicationStatusTestRow?.displayRawStatus ?? ""}
        `);

        if (
            combinedRaw.includes("negative") &&
            (combinedRaw.includes("pass") || combinedRaw.includes("/"))
        ) {
            return "success";
        }

        if (
            combinedRaw.includes("pass") &&
            !combinedRaw.includes("not pass")
        ) {
            return "success";
        }

        if (
            normalizeKey(applicationStatusTestRow?.wellKnownCategory) ===
                "drugtest" &&
            (combinedRaw.includes("pass") ||
                combinedRaw.includes("negative"))
        ) {
            return "success";
        }

        return "danger";
    };

export const resolveCandidateApplicationStatusTestTitle =
    applicationStatusTestRow => {
        const jobName = String(
            applicationStatusTestRow?.jobRequireName ?? ""
        ).trim();

        if (jobName) return jobName;

        const category = String(
            applicationStatusTestRow?.wellKnownCategory ?? ""
        ).trim();

        return category
            ? category.replace(/([a-z])([A-Z])/g, "$1 $2")
            : "Requirement";
    };

export const doesCandidateApplicationStatusTestRowRepresentDrugTest =
    applicationStatusTestRow =>
        normalizeKey(applicationStatusTestRow?.wellKnownCategory) ===
        "drugtest";

export const resolveCandidateApplicationStatusTestBadgeLabel =
    applicationStatusTestRow =>
        String(applicationStatusTestRow?.statusLabel ?? "").trim() ||
        String(applicationStatusTestRow?.displayRawStatus ?? "").trim() ||
        "";

/** `fadvFileDispatch` object on a candidate-test row, or null when absent. */
export const resolveCandidateTestFadvFileDispatch = (
    applicationStatusTestRow = {}
) => {
    const dispatch = applicationStatusTestRow?.fadvFileDispatch;
    if (dispatch == null || typeof dispatch !== "object") return null;
    return dispatch;
};

/** Soft-chip modifier for FADV file-dispatch (pending | success | failed). */
export const resolveCandidateTestFadvFileDispatchChipClassNames = (
    fadvFileDispatch = {}
) => {
    const status = normalize(fadvFileDispatch?.status);

    let tone = "neutral";
    if (status === "success") {
        tone = "success";
    } else if (status === "failed" || status === "fail" || status === "failure") {
        tone = "danger";
    } else if (status === "pending") {
        tone = "neutral";
    }

    return `cd-fadv-dispatch-chip cd-fadv-dispatch-chip--${tone}`;
};

/** Display label for dispatch status (Pending | Success | Failed). */
export const resolveCandidateTestFadvFileDispatchStatusLabel = (
    fadvFileDispatch = {}
) => {
    const status = normalize(fadvFileDispatch?.statusMessage);
    if (status === "success") return "Success";
    if (status === "failed" || status === "fail" || status === "failure") {
        return "Failed";
    }
    if (status === "pending") return "Pending";
    const raw = String(fadvFileDispatch?.statusMessage ?? "").trim();
    return raw || "—";
};

export const doesCandidateApplicationStatusTestRowRepresentVideoInterview =
    applicationStatusTestRow =>
        normalizeKey(applicationStatusTestRow?.wellKnownCategory) ===
        "videointerview";

const parseRawDataObject = rawData => {
    if (rawData == null) return null;
    if (typeof rawData === "object") return rawData;
    if (typeof rawData !== "string") return null;
    const trimmed = rawData.trim();
    if (!trimmed || trimmed.toLowerCase() === "null") return null;
    try {
        const parsed = JSON.parse(trimmed);
        return parsed && typeof parsed === "object" ? parsed : null;
    } catch {
        return null;
    }
};

export const resolveVideoInterviewCompletedLink = (
    applicationStatusTestRow,
    action
) => {
    const edit = getCandidateTestEditNode(applicationStatusTestRow);
    const rawDataObject = parseRawDataObject(edit?.rawData);

    return pickFirstNonEmpty(
        action?.completedLink,
        action?.completed_link,
        applicationStatusTestRow?.completedLink,
        applicationStatusTestRow?.completed_link,
        edit?.completedLink,
        edit?.completed_link,
        rawDataObject?.completedLink,
        rawDataObject?.completed_link
    );
};

const isCompletedStatusValue = value => {
    const normalized = normalize(value);
    return normalized === "completed" || normalized === "complete";
};

export const doesCandidateApplicationStatusTestRowHaveCompletedVideoInterview =
    applicationStatusTestRow => {
        if (
            !doesCandidateApplicationStatusTestRowRepresentVideoInterview(
                applicationStatusTestRow
            )
        ) {
            return false;
        }

        if (
            isCompletedStatusValue(applicationStatusTestRow?.statusLabel) ||
            isCompletedStatusValue(applicationStatusTestRow?.displayRawStatus)
        ) {
            return true;
        }

        const edit = getCandidateTestEditNode(applicationStatusTestRow);

        if (
            isCompletedStatusValue(edit?.rawStatus) ||
            normalize(edit?.status) === "yes" ||
            normalize(applicationStatusTestRow?.statusColorKey) === "yes"
        ) {
            return true;
        }

        return false;
    };

export const doesCandidateTestActionOpenVideoInterviewCompletedLink = (
    applicationStatusTestRow,
    action
) => {
    if (
        !doesCandidateApplicationStatusTestRowRepresentVideoInterview(
            applicationStatusTestRow
        )
    ) {
        return false;
    }

    const link = resolveVideoInterviewCompletedLink(
        applicationStatusTestRow,
        action
    );

    if (!link) return false;

    const label = normalize(action?.label).replace(/\s+/g, " ");
    const kind = normalizeKey(action?.kind);

    if (
        includesAny(label, [
            "view interview link",
            "view interview",
            "view completed interview",
        ])
    ) {
        return true;
    }

    if (label.includes("view") && label.includes("interview")) {
        return true;
    }

    if (
        includesAny(kind, [
            "viewinterviewlink",
            "viewinterview",
            "viewcompletedlink",
            "completedlink",
            "watchvideo",
        ])
    ) {
        return doesCandidateApplicationStatusTestRowHaveCompletedVideoInterview(
            applicationStatusTestRow
        );
    }

    return Boolean(
        pickFirstNonEmpty(action?.completedLink, action?.completed_link)
    );
};

export const shouldShowVideoInterviewCompletedViewLink =
    applicationStatusTestRow => {
        if (
            !doesCandidateApplicationStatusTestRowHaveCompletedVideoInterview(
                applicationStatusTestRow
            )
        ) {
            return false;
        }

        return Boolean(
            resolveVideoInterviewCompletedLink(applicationStatusTestRow)
        );
    };

export const shouldRenderVideoInterviewCompletedViewButton = (
    applicationStatusTestRow,
    actions = []
) => {
    if (!shouldShowVideoInterviewCompletedViewLink(applicationStatusTestRow)) {
        return false;
    }

    const actionList = Array.isArray(actions) ? actions : [];

    return !actionList.some(action =>
        doesCandidateTestActionOpenVideoInterviewCompletedLink(
            applicationStatusTestRow,
            action
        )
    );
};

export const doesCandidateTestActionLabelOpenUpdateFormModal = actionLabel =>
    includesAny(normalize(actionLabel), [
        "add",
        "change",
        "update",
    ]);

export const doesCandidateTestActionLabelOpenDriverDetailsDocumentModal =
    actionLabel => {
        const collapsed = normalize(actionLabel)
            .replace(/['’]/g, "")
            .replace(/[\s_-]+/g, "");

        return includesAny(collapsed, [
            "viewmec",
            "uploadmec",
            "viewdl",
            "uploaddl",
        ]);
    };

export const doesCandidateTestActionLabelRequireSendLinkConfirmation =
    actionLabel => {
        const normalized = normalize(actionLabel).replace(/\s+/g, " ");

        return includesAny(normalized, [
            "send interview link",
            "send avp application link",
            "send mec paperwork",
            "resend mec paperwork",
            "approve",
            "reject"
        ]);
    };

export const doesCandidateTestRowAndLabelOpenRoadTestScheduleChangeModal = (
    applicationStatusTestRow,
    actionLabel
) =>
    normalizeKey(applicationStatusTestRow?.wellKnownCategory) ===
        "roadtest" &&
    includesAny(normalize(actionLabel), ["change"]) &&
    includesAny(normalize(actionLabel), ["schedule"]);

export const buildCandidateTestActionRequestBody = (
    applicationStatusTestRow,
    action,
    candidateIdentifiers
) => ({
    candidateClientId: String(
        candidateIdentifiers?.candidateClientId ?? ""
    ).trim(),
    candidateId: String(
        candidateIdentifiers?.candidateId ?? ""
    ).trim(),
    candidateTestId:
        applicationStatusTestRow?.candidateTestId ?? null,
    jobRequireId:
        applicationStatusTestRow?.jobRequireId ?? null,
    kind: action?.kind ?? null,
});

export const getCandidateTestEditNode = applicationStatusTestRow =>
    typeof applicationStatusTestRow?.edit === "object" &&
    applicationStatusTestRow.edit !== null
        ? applicationStatusTestRow.edit
        : null;

export const resolveCandidateTestMultipartIdentifiers = ({
    applicationStatusTestRow,
    candidateTestActionBodyCandidateClientId,
    candidateTestActionBodyCandidateId,
}) => {
    const edit = getCandidateTestEditNode(
        applicationStatusTestRow
    );

    const row =
        applicationStatusTestRow &&
        typeof applicationStatusTestRow === "object"
            ? applicationStatusTestRow
            : null;

    return {
        candidateClientId: pickFirstNonEmpty(
            candidateTestActionBodyCandidateClientId,
            edit?.candidateClientId,
            row?.candidateClientId
        ),
        candidateId: pickFirstNonEmpty(
            candidateTestActionBodyCandidateId,
            edit?.candidateId,
            row?.candidateId
        ),
        jobRequireId: pickFirstNonEmpty(
            row?.jobRequireId,
            edit?.jobRequireId
        ),
    };
};

export const appendCandidateTestEditEchoFieldsToFormData = (
    formData,
    applicationStatusTestRow
) => {
    const edit = getCandidateTestEditNode(
        applicationStatusTestRow
    );

    if (!edit) return;

    if (Object.prototype.hasOwnProperty.call(edit, "rawStatus")) {
        formData.append(
            "rawStatus",
            edit.rawStatus == null
                ? ""
                : String(edit.rawStatus)
        );
    }

    if (!Object.prototype.hasOwnProperty.call(edit, "rawData")) {
        return;
    }

    const rawData = edit.rawData;

    if (rawData == null) {
        formData.append("rawData", "");
        return;
    }

    if (typeof rawData === "string") {
        formData.append("rawData", rawData);
        return;
    }

    if (typeof rawData === "object") {
        try {
            formData.append(
                "rawData",
                JSON.stringify(rawData)
            );
        } catch {
            formData.append("rawData", "");
        }
    }
};